import { Box, Divider } from '@mui/material';
import React, { useEffect } from 'react';
import { FieldValues, Path, PathValue, useFormContext, useWatch } from 'react-hook-form';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import PathControlledDropdown from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledDropdown';
import PathControlledNumberInput from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/PathControlledNumberInput';
import CloseIconButton from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/CloseIconButton';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { MetricFilterOperatorSchema } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchema';
import { MetricFilterOperatorType } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';

type Props<T extends FieldValues> = {
  groupIndex: number;
  blockIndex: number;
};

const DROPDOWN_WIDTH = '313px';

function MetricFilterSection<T extends FieldValues>({ groupIndex, blockIndex }: Props<T>) {
  const { setValue } = useFormContext<T>();
  const basePath = `queryJSON.rules.ruleGroups.${groupIndex}.ruleBlocks.${blockIndex}.metricFilter` as Path<T>;
  const operator = useWatch({ name: `${basePath}.operator` as Path<T> }) as MetricFilterOperatorType;
  
  // Watch the current field values to check if they need initialization
  const currentRankLimit = useWatch({ name: `${basePath}.rankLimit` as Path<T> });
  const currentValue = useWatch({ name: `${basePath}.value` as Path<T> });

  // Handle operator changes and field initialization
  useEffect(() => {
    if (!operator) return;

    const isRankOperator = operator === MetricFilterOperatorSchema.enum['ranked_in_top'] || operator === MetricFilterOperatorSchema.enum['not_ranked_in_top'];
    const isComparisonOperator = (operator === MetricFilterOperatorSchema.enum['>'] || 
      operator === MetricFilterOperatorSchema.enum['>='] || 
      operator === MetricFilterOperatorSchema.enum['<'] || 
      operator === MetricFilterOperatorSchema.enum['<='] || 
      operator === MetricFilterOperatorSchema.enum['=']);

    if (isRankOperator) {
      // Initialize rankLimit if not set, clear value field
      if (!currentRankLimit) {
        setValue(`${basePath}.rankLimit` as Path<T>, 1 as PathValue<T, Path<T>>);
      }
      if (currentValue !== undefined) {
        setValue(`${basePath}.value` as Path<T>, undefined as PathValue<T, Path<T>>);
      }
    } else if (isComparisonOperator) {
      // Initialize value if not set, clear rankLimit field
      if (currentValue == null) {
        setValue(`${basePath}.value` as Path<T>, 0 as PathValue<T, Path<T>>);
      }
      if (currentRankLimit !== undefined) {
        setValue(`${basePath}.rankLimit` as Path<T>, undefined as PathValue<T, Path<T>>);
      }
    }
  }, [operator, currentRankLimit, currentValue, setValue, basePath]);

  // Only one number input is supported. Map it to the appropriate field based on operator.
  // - ranked_in_top, not_ranked_in_top -> rankLimit
  // - comparison ops -> value
  // - none/between -> no number input
  const getNumberFieldPath = (): Path<T> | undefined => {
    if (!operator) return undefined;
    if (operator === 'ranked_in_top' || operator === 'not_ranked_in_top') return `${basePath}.rankLimit` as Path<T>;
    if (operator === '>' || operator === '>=' || operator === '<' || operator === '<=' || operator === '=') {
      return `${basePath}.value` as Path<T>;
    }
    // 'none' and 'between' have no single numeric input in this simplified UI
    return undefined;
  };

  const numberFieldPath = getNumberFieldPath();
  const showNumberInput = Boolean(numberFieldPath);
  const minValue = operator === 'ranked_in_top' || operator === 'not_ranked_in_top' ? 1 : undefined;

  // Exclude 'between' since UI supports only one numeric input
  const operatorOptions = MetricFilterOperatorSchema.options.filter((opt) => opt !== 'between');

  return (
    <Box sx={{ marginTop: '16px' }}>
      <Divider sx={{ marginBottom: '16px', borderColor: colors.graysSecondary }} />

      <Text variant="body1" sx={{ fontWeight: 600, marginBottom: '16px', display: 'block' }}>
        Metric Filter
      </Text>

      {/* First row with close icon at far right */}
      <Box sx={{ position: 'relative', marginBottom: '16px', paddingRight: '40px' }}>
        <Flex gap={16} alignItems="center">
          <Box sx={{ width: DROPDOWN_WIDTH }}>
            <PathControlledDropdown
              name={`${basePath}.operator` as Path<T>}
              label=""
              options={operatorOptions}
              placeholder="Select Operator"
            />
          </Box>

          {showNumberInput && (
            <Box sx={{ maxWidth: '120px' }}>
              <PathControlledNumberInput
                key={numberFieldPath}
                name={numberFieldPath as Path<T>}
                label=""
                min={minValue as number | undefined}
                max={999999}
              />
            </Box>
          )}
        </Flex>

        {/* Remove this metric filter button */}
        <Box sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
          <CloseIconButton
            onClick={() => (setValue as (p: Path<T>, v: PathValue<T, typeof basePath>) => void)(basePath, undefined)}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default MetricFilterSection;
